import * as _ from "lodash";
import logo from "./shared/Header/images/logo.svg";
import assign from "lodash/assign";
import {
  calibrateTimestampToTimezone,
  formatter,
  setDefaultTimezone,
  localeMap,
} from "./shared/dateTimeUtils";

// config defaults.
const config = {
  locale: "en-US",
  dateFormat: "MDY",
  timeout: 5 * 60 * 1000,
  defaultPrefs: {
    pm: { sweep: true },
  },
  emr: {},
  pm: {
    popoutPreview: false,
    jpegPreview: false,
    allLeadsDefault: false,
    qtc: false,
    snippetRawData: false,
    qrsProcessor: false,
    snippetsEnableDescriptionFreeText: true,
    multiPatientOverride: false,
    pauseOnBlur: false,
    allowMultiPatientSingleLead: true,
    showShiftAssessment: false,
    surveilLeadBufferSeconds: 4,
    training: false,
    forceSnippetPreview: false,
    showTrends: true,
    defaultSnippetDocTypeId: null,
    pdfViewerUrl: "/pdf-viewer",
    zoom: { min: 1, max: 10 },
    mpv: {
      enforceCoreLimit: false,
      singleLeadSeconds: 3,
    },
    preventTimeout: false,
    sweepHideDot: false,
    pollingFrequencyInSeconds: 60,
    canvasWorkerTimeout: 1000,
    snippetApprovalOverride: false,
    showTeleCandidate: false,
    liveModeDelaySeconds: 4,
    hideSideBar: false,
    events: {
      pollingFrequencySeconds: 2,
      count: 500,
    },
  },
  ob: {
    multiPatientOverride: true,
  },
  vitals: {
    reverseChron: true,
  },
  teleReview: {
    enabled: true,
    showMPV: true,
  },
  snippetsReports: {},
  hideAppHeader: false,
  collapseMainNav: false,
  logo, // Accepts a URL.
  multiUserAccount: false,
  kerberosEnabled: false,
  epic: false,
  debug: false,
  hidePatientInfoHeader: false,
  customBedLists: [],
  hideMpv12: true,
  hideMpv16: true,
  hidePHI: false,
  ahi: {
    enabled: false,
    launchUrl: null,
  },
  errorReporting: {
    modalLevel: "error",
    hide: [],
  },
  localPrefs: {
    keyPaths: ["pm.snippet.allLeads", "pm.waveColors"],
    expireDays: 7,
  },
  ENABLE_SITE_SWITCHER: false,
  FF_DARK_MODE: false,
  FF_DANGEROUSLY_HIDE_ERRORS: false,
};

let instanceConfig;

export const extendConfig = (jsonConfig) => {
  instanceConfig = instanceConfig || deepFreeze(_.cloneDeep(jsonConfig));
  setDefaultTimezone(jsonConfig.timezone);
  assign(config, _.defaultsDeep({ ...jsonConfig }, config));
  overrideConfig();
  if (config.base) {
    document.querySelector("base").setAttribute("href", config.base);
  }
  afterExtendConfigCue.forEach((fn) => fn());
  if (process.env.NODE_ENV !== "test") Object.freeze(config);
};

const afterExtendConfigCue = [];
export function afterExtendConfig(fn) {
  afterExtendConfigCue.push(fn);
}

export { instanceConfig };

export default config;

function deepFreeze(object) {
  // Retrieve the property names defined on object
  const propNames = Object.getOwnPropertyNames(object);
  // Freeze properties before freezing self
  for (const name of propNames) {
    const value = object[name];
    if (value && typeof value === "object") {
      deepFreeze(value);
    }
  }
  return Object.freeze(object);
}

afterExtendConfig(function validateConfig() {
  formatter.init();
  // Misconfigurations that will break functionality.
  const misConfigs = {
    [`timezone "${config.timezone}" is invalid. See https://en.wikipedia.org/wiki/List_of_tz_database_time_zones for a list of valid timezones.`]:
      !calibrateTimestampToTimezone(new Date().toISOString()).isValid,
    "pm.zoom should be an object with min and max as numbers":
      typeof config.pm.zoom !== "object" ||
      typeof config.pm.zoom.min !== "number" ||
      typeof config.pm.zoom.max !== "number",
    "enableSecureMessaging is deprecated": config.enableSecureMessaging,
  };
  const failedMsg = Object.entries(misConfigs)
    .filter(([, failed]) => failed)
    .map(([err]) => "Invalid config: " + err)
    .join("\n");
  if (failedMsg) {
    alert(failedMsg);
    throw failedMsg;
  }

  const warnings = {
    "logOutOnBrowserQuit is deprecated, The session will always end when the last window is closed.":
      config.logOutOnBrowserQuit,
  };
  Object.entries(warnings)
    .filter((entry) => entry[1])
    .map(([err]) => "Invalid config: " + err)
    .forEach((err) => console.error(err));

  // Deprecations.
  if (config.preventMonitorTimeout) {
    console.warn(
      '"preventMonitorTimeout" is deprecated. Use "pm"."preventTimeout"',
    );
    config.pm.preventTimeout = true;
  }
  if (!(config.pm.surveilLeadBufferSeconds >= 2)) {
    console.warn(
      "config.pm.surveilLeadBufferSeconds cannot be set to less than 2. provided value:",
      config.pm.surveilLeadBufferSeconds,
    );
    config.pm.surveilLeadBufferSeconds = 2;
  }
  if (!(config.pm.liveModeDelaySeconds >= 2)) {
    console.warn(
      "config.pm.liveModeDelaySeconds cannot be set to less than 2. provided value:",
      config.pm.liveModeDelaySeconds,
    );
    config.pm.liveModeDelaySeconds = 2;
  }
  if (!(config.pm.mpv.singleLeadSeconds >= 3)) {
    console.warn(
      "config.pm.mpv.singleLeadSeconds cannot be less than 3 in sweep mode",
    );
    config.pm.mpv.singleLeadSeconds = 3;
  }
  if (
    config.locale !== localeMap[config.dateFormat] &&
    config.locale === "en-US"
  ) {
    console.warn(
      `config "dateFormat" is deprecated. Please change ' "dateFormat": "${
        config.dateFormat
      }", ', to ' "locale": "${
        localeMap[config.dateFormat]
      }" ', in 'config.json'.`,
    );
    config.locale = localeMap[config.dateFormat];
  }
});

const configOverrides = {
  embedded_atc: {
    hideAppHeader: true,
    collapseMainNav: true,
    hidePatientInfoHeader: true,
    pm: {
      hideSideBar: true,
      showTrends: false,
    },
  },
};

const _mode = new URLSearchParams(location.search).get("mode");
export const mode = configOverrides[_mode] ? _mode : null;

function overrideConfig() {
  if (mode) {
    assign(config, _.defaultsDeep(configOverrides[mode], config));
  }
}
